import React, { useState } from "react";
import { Col, Div, Row, Text } from "../../styles/Common";
import { LoginWrapper } from "../Login/styles";
import logo from "../../assets/images/logo.png";
import { Card } from "../../components/Card";
import { theme } from "../../styles/theme";
import { Button } from "../../components/Button";
import { Password } from "../../components/Password";
import useResetPassword from "./hooks/useResetPassword";
import { validateResetPassword } from "../../utilities/validations";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal } from "../../components/Modal";
import { Alert } from "antd";
import { Link } from "../../components/Link";
import success from "../../assets/icons/success.svg";
import { useMediaQuery } from "react-responsive";
import PasswordRequirements from "./components/PasswordRequirements";

export const ResetPassword = () => {
  const isMobile = useMediaQuery({
    query: `(max-width: ${theme.breakpoints.sm})`,
  });
  const resetPassword = useResetPassword();
  const { isLoading } = resetPassword;
  const navigate = useNavigate();
  const { search } = useLocation();
  const hash = new URLSearchParams(search).get("hash");
  const [passwordForm, setPasswordForm] = useState({
    password: "",
    passwordConfirm: "",
  });
  const [errorForm, setErrorForm] = useState({
    password: {
      error: false,
      message: "",
    },
    passwordConfirm: {
      error: false,
      message: "",
    },
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [unmetRequirements, setUnmetRequirements] = useState([]);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const handleChange = (event) => {
    const { value, id } = event.target;

    const newErrorForm = errorForm;
    newErrorForm[id].error = false;
    newErrorForm[id].message = "";
    setErrorForm(newErrorForm);

    const newPasswordForm = passwordForm;
    newPasswordForm[id] = value;
    setPasswordForm(newPasswordForm);
    setForceUpdate(!forceUpdate);
  };

  const handleReset = () => {
    setHasAttemptedSubmit(true);
    
    const validation = validateResetPassword.validate(passwordForm, {
      abortEarly: false,
    });

    if (validation.error) {
      const newErrorForm = errorForm;
      validation.error.details.forEach((ele) => {
        newErrorForm[ele.context.label].error = true;
        newErrorForm[ele.context.label].message =
          "Required field or invalid format";
        setErrorForm(newErrorForm);
        setForceUpdate(!forceUpdate);
      });
    } else {
      resetPassword.reset();
      resetPassword.mutate(
        { ...passwordForm, hash },
        {
          onSuccess: () => {
            setShowSuccess(true);
            setErrorMessage("");
          },
          onError: (err) => {
            setErrorMessage(err.response.data.Errors[0]);
          },
        }
      );
    }
  };
  const handleClose = () => {
    setShowSuccess(false);
    navigate("/");
  };

  const handleLogin = () => {
    navigate("/");
  };

  const handlePasswordValidationChange = React.useCallback((isValid, unmetReqs) => {
    setIsPasswordValid(isValid);
    setUnmetRequirements(unmetReqs);
  }, []);

  const getPasswordHelperMessage = () => {
    if (!hasAttemptedSubmit) {
      return "";
    }
    
    if (!passwordForm.password) {
      return "Password is required";
    }
    
    if (unmetRequirements.length === 0) {
      return "";
    }
    
    if (unmetRequirements.length === 1) {
      const requirement = unmetRequirements[0];
      switch (requirement.key) {
        case "length":
          return "Password must be at least 12 characters long";
        case "lowercase":
          return "Password must include at least one lowercase letter";
        case "uppercase":
          return "Password must include at least one uppercase letter";
        case "number":
          return "Password must include at least one number";
        case "special":
          return "Password must include at least one special character (#?!@$%^&*-)";
        default:
          return requirement.text;
      }
    }
    
    const requirementMessages = unmetRequirements.map(req => {
      switch (req.key) {
        case "length":
          return "at least 12 characters long";
        case "lowercase":
          return "at least one lowercase letter";
        case "uppercase":
          return "at least one uppercase letter";
        case "number":
          return "at least one number";
        case "special":
          return "at least one special character (#?!@$%^&*-)";
        default:
          return req.text.toLowerCase();
      }
    });
    
    if (requirementMessages.length === 1) {
      return `Password must be ${requirementMessages[0]}`;
    } else if (requirementMessages.length === 2) {
      return `Password must be ${requirementMessages[0]} and ${requirementMessages[1]}`;
    } else {
      const lastRequirement = requirementMessages.pop();
      const otherRequirements = requirementMessages.join(", ");
      return `Password must be ${otherRequirements}, and ${lastRequirement}`;
    }
  };

  const getPasswordConfirmHelperMessage = () => {
    if (!hasAttemptedSubmit) {
      return "";
    }
    
    if (!passwordForm.passwordConfirm) {
      return "Please confirm your password";
    }
    
    if (passwordForm.password !== passwordForm.passwordConfirm) {
      return "Passwords do not match";
    }
    
    return "";
  };

  return (
    <LoginWrapper>
      <Modal
        open={showSuccess}
        onCancel={handleClose}
        align={"center"}
        padding={36}
      >
        <Modal.Header
          icon={success}
          iconBackground={theme.colors.dark}
          iconWidth={"24px"}
          iconHeight={"24px"}
          align={"-webkit-center"}
          padding="16px 0 16px 0"
          title={"New Password Successful"}
        />
        <Modal.Body margin="16px 0 24px 0" maxHeight={200}>
          <Text
            size={theme.fonts.size.default}
            weight={theme.fonts.weight.medium}
            color={theme.colors.grey1}
          >
            You can now log in with your email and new password
          </Text>
        </Modal.Body>
      </Modal>
      <Row width="1200px" height="700px">
        {!isMobile && (
          <Col width="50%" height="100%">
            <img alt="logo" src={logo} width="336px" height="143px" />
          </Col>
        )}
        <Col width={isMobile ? "100%" : "50%"} height="100%" align="center" justify="center">
          <Card width="380px">
            <Div direction="column" align="center" width="100%">
              {isMobile && (
                <img 
                  alt="logo" 
                  src={logo} 
                  width="161px" 
                  height="76px" 
                  style={{ marginBottom: "28px" }}
                />
              )}
              <Text
                color={theme.colors.dark2}
                weight={theme.fonts.weight.bold}
                size={theme.fonts.size.h3}
                mb="10px"
              >
                Reset Password
              </Text>
              <Div mb="28px" width="100%">
                <PasswordRequirements 
                  password={passwordForm.password} 
                  onValidationChange={handlePasswordValidationChange}
                />
              </Div>
              <Div direction="column" gap="16px" width="287px" m="12px 0 0 0">
                <Password
                  placeholder="New Password"
                  id="password"
                  onChange={handleChange}
                  error={hasAttemptedSubmit && !isPasswordValid && passwordForm.password}
                  helper={getPasswordHelperMessage()}
                />
                <Password
                  placeholder="Password Again"
                  id="passwordConfirm"
                  onChange={handleChange}
                  error={hasAttemptedSubmit && passwordForm.passwordConfirm && passwordForm.password !== passwordForm.passwordConfirm}
                  helper={getPasswordConfirmHelperMessage()}
                />
              </Div>
              {errorMessage !== "" && (
                <Div
                  m="20px 0 0 0"
                  width="100%"
                  justify="flex-end"
                  style={{ alignSelf: "end" }}
                >
                  <Alert closable message={`${errorMessage}`} type="error" onClose={() => setErrorMessage("")} />
                </Div>
              )}
              <Button
                onClick={handleReset}
                width="100%"
                loading={isLoading}
                background={theme.colors.salmon}
                style={{ marginTop: "32px" }}
              >
                Reset Password
              </Button>
              <Div>
                <Link size={theme.fonts.size.sm} onClick={handleLogin}>
                  Back to Login
                </Link>
              </Div>
            </Div>
          </Card>
        </Col>
      </Row>
    </LoginWrapper>
  );
};
